import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { OFFICES, CONTACT, buildWhatsAppLink } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=85&w=2400";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone.");
      return;
    }
    const text = `Hello NEPHRC,

I'd like to connect.

Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone}
Subject: ${form.subject || "—"}

Message:
${form.message || "—"}`;
    window.open(buildWhatsAppLink(text), "_blank");
    toast.success("Opening WhatsApp — our team will respond shortly.");
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Get in touch"
        title={<>Let&apos;s engineer your<br /><span className="italic font-light text-gold">next milestone.</span></>}
        subtitle="Five offices across India and a 24/7 WhatsApp line — we are easy to reach and quick to respond."
        image={HERO_IMG}
        breadcrumbs={[{ label: "Contact" }]}
      />

      <section className="bg-white py-24">
        <div className="container-x grid md:grid-cols-3 gap-px bg-slate-200">
          <a href={`tel:${CONTACT.primaryPhone}`} className="bg-white p-8 hover:bg-mist transition-colors" data-testid="contact-phone-card">
            <Phone className="text-gold" size={22} />
            <div className="overline text-emerald-brand mt-5">Call us</div>
            <div className="mt-2 space-y-1">
              {CONTACT.phones.map((p) => (
                <div key={p} className="font-display font-semibold text-navy">{p}</div>
              ))}
            </div>
          </a>
          <a href={`mailto:${CONTACT.email}`} className="bg-white p-8 hover:bg-mist transition-colors" data-testid="contact-email-card">
            <Mail className="text-gold" size={22} />
            <div className="overline text-emerald-brand mt-5">Email us</div>
            <div className="mt-2 font-display font-semibold text-navy">{CONTACT.email}</div>
          </a>
          <a href={buildWhatsAppLink("Hello NEPHRC, I'd like to discuss a project.")} target="_blank" rel="noreferrer" className="bg-white p-8 hover:bg-mist transition-colors" data-testid="contact-whatsapp-card">
            <MessageCircle className="text-gold" size={22} />
            <div className="overline text-emerald-brand mt-5">WhatsApp</div>
            <div className="mt-2 font-display font-semibold text-navy">{CONTACT.phones[0]}</div>
            <div className="text-xs text-slate-brand mt-1">24/7 instant response</div>
          </a>
        </div>
      </section>

      <section className="bg-mist py-24 sm:py-32">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-emerald-brand">Send an enquiry</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-3 leading-[1.05]">
              Tell us about your project.
            </h2>
            <p className="mt-6 text-slate-brand leading-relaxed">
              Share the brief below — the form opens a pre-filled WhatsApp message so our team can respond immediately on the channel you prefer.
            </p>

            <div className="mt-10 space-y-5">
              {CONTACT.phones.map((p) => (
                <div key={p} className="flex items-center gap-3 text-slate-brand">
                  <Phone size={16} className="text-emerald-brand" />
                  <a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-navy">{p}</a>
                </div>
              ))}
              <div className="flex items-center gap-3 text-slate-brand">
                <Mail size={16} className="text-emerald-brand" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-navy">{CONTACT.email}</a>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-white p-8 sm:p-10 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline text-emerald-brand block mb-2">Full Name *</label>
                <Input data-testid="contact-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-300" />
              </div>
              <div>
                <label className="overline text-emerald-brand block mb-2">Phone *</label>
                <Input data-testid="contact-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border-slate-300" />
              </div>
              <div>
                <label className="overline text-emerald-brand block mb-2">Email</label>
                <Input data-testid="contact-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-300" />
              </div>
              <div>
                <label className="overline text-emerald-brand block mb-2">Subject</label>
                <Input data-testid="contact-form-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border-slate-300" />
              </div>
            </div>
            <div>
              <label className="overline text-emerald-brand block mb-2">Project Brief *</label>
              <Textarea data-testid="contact-form-message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="border-slate-300" />
            </div>
            <button
              type="submit"
              data-testid="contact-form-submit"
              className="inline-flex items-center gap-2 px-7 py-4 bg-gold text-navy font-semibold text-xs tracking-[0.2em] uppercase hover:bg-[#E8C45F] transition-colors"
            >
              <Send size={14} /> Send Enquiry
            </button>
          </form>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="container-x">
          <div className="overline text-emerald-brand">Our offices</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-3 leading-[1.05]">
            Five offices across India.
          </h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {OFFICES.map((o, i) => (
              <motion.a
                key={o.address}
                href={`https://www.google.com/maps/search/${encodeURIComponent(o.mapQuery)}`}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                className="group p-7 border border-slate-200 hover:border-navy transition-colors"
                data-testid={`office-card-${o.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-gold" />
                  <span className="overline text-emerald-brand">{o.city}</span>
                </div>
                <div className="mt-4 font-display font-bold text-navy text-lg">{o.name}</div>
                <p className="mt-3 text-sm text-slate-brand leading-relaxed">{o.address}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-navy group-hover:text-emerald-brand transition-colors">
                  Open in Maps
                </div>
              </motion.a>
            ))}
          </div>

          <div className="mt-12 aspect-[16/7] w-full overflow-hidden border border-slate-200">
            <iframe
              title="NEPHRC India map"
              src="https://www.google.com/maps?q=India&output=embed&z=5"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
